import React, { useState, useEffect } from 'react';
import { Building2, Search, Download, RefreshCw, AlertTriangle, X, CheckCircle, Eye, Edit, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Menu,
  Button
} from '@mui/material';
import MainService from '../../services/MainService';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../services/appSettings';

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchKeyword, setActiveSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    loadClients(activeSearchKeyword);
  }, [currentPage, activeSearchKeyword, pageSize]);

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadClients = async (keyword = '') => {
    try {
      setLoading(true);
      setError('');
      
      // If "All" is selected, first get total count, then fetch all records
      if (pageSize === 'all') {
        // First request to get total count
        const countRequestData = {
          Keyword: keyword || '',
          Skip: 0,
          Take: 1
        };
        const countResponse = await MainService.ClientsInList(countRequestData);
        const total = countResponse?.Total || 0;
        
        if (total > 0) {
          // Now fetch all records
          const allRequestData = {
            Keyword: keyword || '',
            Skip: 0,
            Take: total
          };
          const allResponse = await MainService.ClientsInList(allRequestData);
          
          if (allResponse && allResponse.Data) {
            setClients(allResponse.Data || []);
            setTotalCount(total);
          } else {
            setClients([]);
            setTotalCount(0);
          }
        } else {
          setClients([]);
          setTotalCount(0);
        }
      } else {
        const skip = (currentPage - 1) * pageSize;
        const requestData = {
          Keyword: keyword || '',
          Skip: skip,
          Take: pageSize
        };

        const response = await MainService.ClientsInList(requestData);
        
        if (response && response.Data) {
          setClients(response.Data || []);
          setTotalCount(response.Total || 0);
        } else {
          setClients([]);
          setTotalCount(0);
        }
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
      setClients([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setActiveSearchKeyword(searchTerm);
  };

  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchTerm('');
    setActiveSearchKeyword('');
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const exportToExcel = () => {
    if (clients.length === 0) {
      setError('No clients to export');
      return;
    }

    try {
      const exportData = clients.map((client, index) => ({
        '#': pageSize === 'all' ? index + 1 : (currentPage - 1) * pageSize + index + 1,
        'Client No': client.ClientNo || 'N/A',
        'Name': client.Name || 'N/A',
        'Mobile No1': client.MobileNo1 || 'N/A',
        'Mobile No2': client.MobileNo2 || 'N/A',
        'Tel No': client.TelNo || 'N/A',
        'Email': client.EmailId || 'N/A',
        'Company': client.Company || 'N/A',
        'Delivery Charges': client.DeliveryCharges || 'N/A',
        'Tax Percentage': client.TaxPercentage || 'N/A',
        'Location': client.LocationName || 'N/A',
        'Emirate': client.EmirateName || 'N/A',
        'Area': client.AreaName || 'N/A',
        'Address': client.Address || 'N/A',
        'PO Box': client.POBOX || 'N/A',
        'Responsible By': client.ResponsibleBy?.Name || 'N/A',
        'Pay Mode': client.PayMode || 'N/A',
        'Date': client.Date ? new Date(client.Date).toLocaleDateString('en-GB') : 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Clients');
      const fileName = `clients_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setSuccess('Excel file exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export Excel file');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleContextMenu = (event, client) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setContextMenu(null);
    setSelectedClient(null);
  };

  const handleView = (client) => {
    handleMenuClose();
    // TODO: Implement view functionality
    console.log('View client:', client);
  };

  const handleEdit = (client) => {
    handleMenuClose();
    navigate(`/home/accounts/addClient/${client.Id}`);
  };

  const handleDelete = (client) => {
    handleMenuClose();
    // TODO: Implement delete functionality
    console.log('Delete client:', client);
  };

  const sortClients = (data) => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'ClientNo':
          aValue = a.ClientNo || '';
          bValue = b.ClientNo || '';
          break;
        case 'Name':
          aValue = a.Name || '';
          bValue = b.Name || '';
          break;
        case 'MobileNo1':
          aValue = a.MobileNo1 || '';
          bValue = b.MobileNo1 || '';
          break;
        case 'EmailId':
          aValue = a.EmailId || '';
          bValue = b.EmailId || '';
          break;
        case 'Company':
          aValue = a.Company || '';
          bValue = b.Company || '';
          break;
        case 'Location':
          aValue = a.LocationName || '';
          bValue = b.LocationName || '';
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedClients = sortClients(clients);

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalCount / pageSize);

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUp className="w-3 h-3 opacity-30" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-3 h-3" style={{ color: '#46955A' }} />
      : <ArrowDown className="w-3 h-3" style={{ color: '#46955A' }} />;
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center mb-2">
              <Building2 className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
            </div>
            <Button
              variant="contained"
              startIcon={<Building2 className="w-4 h-4" />}
              onClick={() => navigate('/home/accounts/addClient')}
              sx={{ backgroundColor: '#46955A', '&:hover': { backgroundColor: '#3d7d4f' } }}
            >
              Add Client
            </Button>
          </div>

          {/* Search and Actions */}
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="flex-1">
              <TextField
                fullWidth
                placeholder="Search by name or mobile number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="w-5 h-5 text-gray-400" />
                    </InputAdornment>
                  ),
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                      borderBottom: '1px solid #E5E7EB'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderBottom: '2px solid #46955A'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderBottom: '2px solid #46955A'
                    }
                  }
                }}
                variant="outlined"
              />
            </div>
            <div className="flex items-center gap-2">
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    color: '#46955A',
                    '&:hover': { backgroundColor: '#f0f9f4' },
                    '&:disabled': { color: '#9ca3af' }
                  }}
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <IconButton
                  onClick={exportToExcel}
                  disabled={clients.length === 0}
                  sx={{
                    color: '#46955A',
                    '&:hover': { backgroundColor: '#f0f9f4' },
                    '&:disabled': { color: '#9ca3af' }
                  }}
                >
                  <Download className="w-5 h-5" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            height: '500px',
            position: 'relative',
            border: '1px solid #E5E7EB',
            borderRadius: '0.5rem',
            '& .MuiPaper-root': {
              boxShadow: 'none'
            }
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 20
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={32} sx={{ color: '#46955A' }} />
                <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Loading...</span>
              </Box>
            </Box>
          )}
          <Table stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '5%', padding: '8px' }}>#</TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '10%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('ClientNo')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Client No
                    <SortIcon field="ClientNo" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '15%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('Name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Name
                    <SortIcon field="Name" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('MobileNo1')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Mobile No1
                    <SortIcon field="MobileNo1" />
                  </Box>
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>Mobile No2</TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '15%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('EmailId')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Email
                    <SortIcon field="EmailId" />
                  </Box>
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>Company</TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '10%', padding: '8px' }}>Location</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '8%', padding: '8px' }}>Photo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedClients.length > 0 ? (
                sortedClients.map((client, index) => (
                  <TableRow
                    key={client.Id || index}
                    hover
                    onContextMenu={(e) => handleContextMenu(e, client)}
                    sx={{
                      '&:hover': { backgroundColor: '#F9FAFB' },
                      '&:last-child td, &:last-child th': { border: 0 },
                      cursor: 'context-menu'
                    }}
                  >
                    <TableCell align="center" sx={{ fontSize: '0.875rem', color: '#6B7280', padding: '8px' }}>
                      {pageSize === 'all' 
                        ? index + 1 
                        : (currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827', padding: '8px' }}>
                      {client.ClientNo || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827', padding: '8px' }}>
                      {client.Name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {client.MobileNo1 || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {client.MobileNo2 || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {client.EmailId || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {client.Company || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {client.LocationName || 'N/A'}
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#F3F4F6',
                          border: '1px solid #E5E7EB'
                        }}
                      >
                        {client.Photo ? (
                          <img 
                            src={
                              client.Photo && client.Photo.startsWith('http') 
                                ? client.Photo 
                                : `${getApiBaseUrl()}${client.Photo?.startsWith('/') ? client.Photo.slice(1) : client.Photo}`
                            } 
                            alt={client.Name || 'Client'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNGM0Y0RjYiLz4KPHBhdGggZD0iTTIwIDEyQzE3LjI0IDEyIDE1IDE0LjI0IDE1IDE3QzE1IDE5Ljc2IDE3LjI0IDIyIDIwIDIyQzIyLjc2IDIyIDI1IDE5Ljc2IDI1IDE3QzI1IDE0LjI0IDIyLjc2IDEyIDIwIDEyWk0yMCAyNEMxNS41OCAyNCAxMiAyNy41OCAxMiAzMkgxNkMxNiAyOS43OSAxNy43OSAyOCAyMCAyOEMyMi4yMSAyOCAyNCAyOS43OSAyNCAzMkgyOEMyOCAyNy41OCAyNC40MiAyNCAyMCAyNFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                            }}
                          />
                        ) : (
                          <Building2 className="w-5 h-5 text-gray-400" />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Building2 className="w-8 h-8 text-gray-400" />
                      <Box sx={{ color: '#6B7280' }}>
                        {searchTerm ? `No clients found for "${searchTerm}"` : 'No clients available'}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          PaperProps={{
            sx: {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              borderRadius: '0.5rem',
              minWidth: 150,
              mt: 0.5
            }
          }}
        >
          <MenuItem
            onClick={() => handleView(selectedClient)}
            sx={{
              fontSize: '0.875rem',
              py: 1.5,
              '&:hover': { backgroundColor: '#f0f9f4' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Eye className="w-4 h-4" style={{ color: '#46955A' }} />
              <span>View</span>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleEdit(selectedClient)}
            sx={{
              fontSize: '0.875rem',
              py: 1.5,
              '&:hover': { backgroundColor: '#f0f9f4' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Edit className="w-4 h-4" style={{ color: '#46955A' }} />
              <span>Edit</span>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleDelete(selectedClient)}
            sx={{
              fontSize: '0.875rem',
              py: 1.5,
              color: '#DC2626',
              '&:hover': { backgroundColor: '#FEE2E2' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </Box>
          </MenuItem>
        </Menu>

        {/* Footer with Pagination and Row Selection */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2, 
          borderTop: '1px solid #E5E7EB',
          gap: 2
        }}>
          {/* Count Display */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
              {totalCount > 0 
                ? pageSize === 'all'
                  ? `1-${totalCount} out of ${totalCount}`
                  : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} out of ${totalCount}`
                : '0 out of 0'
              }
            </span>
            
            {/* Rows per page selection */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Rows per page:</span>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#46955A'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#46955A'
                    }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#6B7280'
                },
                '& .Mui-selected': {
                  backgroundColor: '#46955A !important',
                  color: '#fff !important',
                  '&:hover': {
                    backgroundColor: '#3d7d4f !important'
                  }
                },
                '& .MuiPaginationItem-root:hover': {
                  backgroundColor: '#f0f9f4'
                }
              }}
            />
          )}
        </Box>
      </div>
    </div>
  );
};

export default Clients;

